import styled, { css, keyframes } from 'styled-components';

export const Search = styled.input`
  width: 100%;
  margin-bottom: 12px;
`;

export const Contents = styled.ol`
  margin: 0;
  padding-left: 20px;
  line-height: 1.7;

  a {
    color: var(--ink);
  }
`;

export const PartTitle = styled.h2`
  margin: 24px 0 8px;
  color: var(--accent);
  font-size: 20px;
`;

export const Pages = styled.span`
  display: block;
  margin-top: 2px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 400;
`;

/** The rulebook's own text, rendered from its transcription. */
export const Body = styled.div`
  display: flow-root;
  line-height: 1.55;

  p, ul, ol, table, blockquote {
    margin: 0 0 10px;
  }

  h3 {
    margin: 14px 0 6px;
    font-size: 16px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  th, td {
    padding: 4px 6px;
    border-bottom: 1px solid var(--line);
    text-align: left;
  }

  blockquote {
    display: flow-root;
    padding: 8px 12px;
    border-left: 3px solid var(--accent);
    background: rgba(0, 0, 0, .2);
  }

  /* Diagrams sit full width on their own paper, like the booklet page. Tapping a picture opens it full screen. */
  img {
    cursor: zoom-in;
    display: block;
    max-width: 100%;
    max-height: 420px;
    margin: 12px auto;
    border-radius: 6px;
  }

  /*
   * Illustrations sit beside the text as they do in the booklet: the page file ends the picture's path with
   * #left or #right. They are cut out of the page, so they need no frame.
   */
  img[src$="#left"],
  img[src$="#right"] {
    width: 40%;
    max-width: 220px;
    max-height: none;
    border-radius: 0;
  }

  img[src$="#left"] {
    float: left;
    margin: 4px 16px 8px 0;
  }

  img[src$="#right"] {
    float: right;
    margin: 4px 0 8px 16px;
  }

  /* Pictures printed side by side, like Vileplume and Ivysaur, stay side by side. Energy symbols in a sentence
     are images too, so they're left out. */
  p:has(> img:not([src$="#energy"]) + img:not([src$="#energy"])) {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 12px;
  }

  p:has(> img:not([src$="#energy"]) + img:not([src$="#energy"])) > img {
    flex: 1 1 0;
    min-width: 0;
    margin: 0;
    border-radius: 0;
  }

  /* Energy symbols sit in the line of text, as printed. */
  img[src$="#energy"] {
    display: inline-block;
    width: 1.15em;
    height: 1.15em;
    margin: 0 1px;
    vertical-align: -0.2em;
    border-radius: 50%;
    cursor: default;
  }

  /* Every tip box in the book has the same small portrait beside it. */
  blockquote img:not([src$="#energy"]) {
    cursor: default;
    float: left;
    width: 44px;
    margin: 2px 10px 4px 0;
  }
`;

/** The book view gets the whole width for two-page spreads; the text stays a readable column. */
export const RulesPage = styled.main`
  max-width: ${props => props.$wide ? '1400px' : '560px'};
  margin: 0 auto;
  padding: 16px 16px calc(24px + env(safe-area-inset-bottom));
`;

export const RulesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  h1 {
    margin-bottom: 12px;
  }

  a {
    text-decoration: none;
  }
`;

export const BookNav = styled.nav`
  display: grid;
  grid-template-columns: auto minmax(0, 220px) auto;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

/**
 * One or two pages, each as tall as the screen allows unless that would be too wide. Every page has the same shape
 * ($width × $height), so the size is set before a photo loads and nothing shifts when it arrives. The perspective
 * gives a turning page its depth.
 */
export const Spread = styled.div`
  --pages: ${props => props.$pages};
  --page-ratio: calc(${props => props.$height} / ${props => props.$width});
  --page-height: min(calc(100dvh - 190px), calc(100cqw / var(--pages) * var(--page-ratio)));
  container-type: inline-size;
  display: grid;
  grid-template-columns: repeat(${props => props.$pages}, minmax(0, max-content));
  justify-content: center;
  perspective: 2400px;
  touch-action: pan-y pinch-zoom;
`;

/**
 * How a page looks on its side of the open booklet: the two pages meet flush at the spine, so only the outer
 * corners are rounded and the shadow falls outward. A single page (on a phone) is rounded all round.
 */
const sideLook = (side) => {
  if (side === 'left') return css`--radius: 4px 0 0 4px; --shadow: -4px 3px 10px -3px rgba(0, 0, 0, .55);`;
  if (side === 'right') return css`--radius: 0 4px 4px 0; --shadow: 4px 3px 10px -3px rgba(0, 0, 0, .55);`;
  return css`--radius: 4px; --shadow: 0 2px 10px rgba(0, 0, 0, .5);`;
};

/** One side of the open booklet ($side left or right; none for a single page); a turning page lies on top of it. */
export const Side = styled.div`
  position: relative;
  ${props => sideLook(props.$side)}
`;

/** How long a page takes to turn over. */
const TURN = '600ms';

const turnOverLeft = keyframes`
  from { transform: rotateY(0deg); }
  to { transform: rotateY(-180deg); }
`;

const turnOverRight = keyframes`
  from { transform: rotateY(0deg); }
  to { transform: rotateY(180deg); }
`;

const turnIn = keyframes`
  from { transform: rotateY(-180deg); }
  to { transform: rotateY(0deg); }
`;

/**
 * The page being turned, hinged on its spine edge ($edge). It turns over away from the spine, or with $in comes
 * back over onto the page (a phone going back a page).
 */
export const Leaf = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  transform-style: preserve-3d;
  transform-origin: ${props => props.$edge} center;
  animation: ${props => {
    if (props.$in) return turnIn;
    return props.$edge === 'left' ? turnOverLeft : turnOverRight;
  }} ${TURN} ease-in-out forwards;
  pointer-events: none;
`;

/*
 * Each face of the turning page shows only while it faces you: the front until the page stands edge-on halfway
 * through, the back after. Switching visibility at that moment, instead of trusting backface-visibility, works in
 * every browser (some render the hidden side mirrored).
 */
const visibleFirstHalf = keyframes`
  0%, 49.9% { visibility: visible; }
  50%, 100% { visibility: hidden; }
`;

const visibleSecondHalf = keyframes`
  0%, 49.9% { visibility: hidden; }
  50%, 100% { visibility: visible; }
`;

const face = css`
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

/** The page you see as it starts to turn; with $in (turning back in) it's the one that arrives instead. */
export const FrontFace = styled.div`
  ${face}
  animation: ${props => props.$in ? visibleSecondHalf : visibleFirstHalf} ${TURN} linear forwards;
`;

/** The other side of the turning page, the page it shows once it has turned over; $side is where it lands. */
export const BackFace = styled.div`
  ${face}
  ${props => sideLook(props.$side)}
  transform: rotateY(180deg);
  animation: ${visibleSecondHalf} ${TURN} linear forwards;
`;

/** The back of a turning page that has no printed page there, like the inside of the cover. */
export const PaperBack = styled.div`
  border-radius: var(--radius);
  background: #f3efe4;
  box-shadow: var(--shadow);
`;

/** A booklet page is a button: tapping it opens the page full screen. */
export const PageButton = styled.button`
  display: block;
  padding: 0;
  border: 0;
  background: none;
  cursor: zoom-in;
`;

export const PageImage = styled.img`
  display: block;
  height: var(--page-height);
  width: auto;
  aspect-ratio: ${props => props.width} / ${props => props.height};
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  ${props => props.$blank && 'visibility: hidden;'}
`;

export const TextLink = styled.p`
  margin: 12px 0 0;
  text-align: center;

  a {
    color: var(--accent);
    font-weight: 600;
  }
`;

/** The full-screen image viewer: the page or picture, zoomable, over a dark backdrop. */
export const ViewerStage = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  /* Flex, not grid: a grid's auto row has no height, so the image's max-height: 100% would be ignored and a tall
     page would overflow the screen at 1×. */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: calc(var(--top-clear) + 8px) 8px calc(env(safe-area-inset-bottom) + 8px);
  background: rgba(0, 0, 0, .97);
  touch-action: none;
  user-select: none;
`;

export const ViewerImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  flex: none;
  border-radius: 4px;
  transform-origin: center;
  will-change: transform;
  cursor: ${props => props.$zoomed ? 'grab' : 'zoom-in'};
`;

export const ViewerButtons = styled.div`
  position: absolute;
  top: calc(var(--top-clear) + 10px);
  right: 10px;
  display: flex;
  gap: 8px;
`;

export const ViewerButton = styled.button`
  width: 44px;
  height: 44px;
  /* Dark and solid with a light ring, so the buttons show over the dark backdrop and over a zoomed-in white page. */
  border: 1.5px solid rgba(255, 255, 255, .55);
  border-radius: 50%;
  background: rgba(20, 20, 20, .82);
  box-shadow: 0 2px 8px rgba(0, 0, 0, .45);
  color: #fff;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    color: rgba(255, 255, 255, .35);
    cursor: default;
  }
`;
